import * as H from 'history';

import sortBy from 'lodash/sortBy';

import { ALL_LOCALES_FOR_DATA_CENTER, DEFAULT_LANGUAGE_CODE, DEFAULT_LOCALE, DataCenterCode, Locale, getDataCenter } from './Locales';
import { ensureNoTrailingSlash } from './Urls';

// localstorage key for new redirection and routing logic
export const NEW_REDIRECT_ROUTE = 'newRedirectRoute';

export function getRoutesForLocale(locale: Locale): string[] {
  const lowercaseCountryCode = locale.country.code.toLowerCase();
  const routePath = `/${lowercaseCountryCode}/${locale.languageCode}`;
  const shortenedRoute = `/${lowercaseCountryCode}`;
  const isDefaultLangForCountry =
    locale.country.languages[0].code === locale.languageCode;

  const routes = [];

  // Setup default route of '/' for the default data center without any country or language in the url
  if (locale.code === DEFAULT_LOCALE.code) {
    routes.push('');
  }

  if (isDefaultLangForCountry) {
    routes.push(shortenedRoute);
  }

  routes.push(routePath);

  return routes;
}

export const useRoutesByDataCenter = (locales: Locale[]): [Record<string, Locale>, Record<string, string>] => {
  // sorting ensures a consistent ordering of locales
  locales = sortBy(locales, ['country.code', 'language.code']);
  const redirectRoutes: Record<string, string> = {};
  const routeToLocale: Record<string, Locale> = {};

  if (getDataCenter().code !== DataCenterCode.NORTH_AMERICA) {
    // For non-NA data centers just find the first locale which is associated with the default language
    const fallbackLocale = locales.find(locale =>
      locale.languageCode === DEFAULT_LANGUAGE_CODE && locale.country.languages[0].code === DEFAULT_LANGUAGE_CODE
    );
    if (fallbackLocale) {
      redirectRoutes[''] = `/${fallbackLocale.country.code.toLowerCase()}`;
    }
  }

  locales.forEach(locale => {
    // Get all valid pathnames for the locale. The first is the primary one we want.
    const [rootPath, ...secondaryPaths] = getRoutesForLocale(locale);
    routeToLocale[ensureNoTrailingSlash(rootPath).toLowerCase()] = locale;
    // Have all valid secondary paths redirect to the main path for the locale
    secondaryPaths.forEach(path => redirectRoutes[path] = rootPath);
  });

  // sort routeToLocale in reverse order to provide exact match for react route
  const sortRouteToLocale = Object.keys(routeToLocale)
    .sort((a, b) => b.length - a.length)
    .reduce((result, key) => {
      result[key] = routeToLocale[key];

      return result;
    }, {});

  return [sortRouteToLocale, redirectRoutes];
};

export const redirectToUrl = (toBaseRoute: string, pageName: string | undefined, location: H.Location) => {
  pageName = pageName ? `/${pageName}` : '';

  return pageName.startsWith(toBaseRoute) ?
    `${pageName}${location.search}` :
    `${toBaseRoute}${pageName}${location.search}`;
};

export const getBaseUrlFromLocale = (localeCode: string) => {
  const [routeToLocale] = useRoutesByDataCenter(ALL_LOCALES_FOR_DATA_CENTER);

  return Object.keys(routeToLocale).find(route => routeToLocale[route].code === localeCode);
};

export const contains = (str: string | undefined, search: string) => {
  if (!str) return false;

  return str.indexOf(search) === 0;
};
